import React from 'react';
import { BadgeProps } from '@frontend/shadcn/components/ui/badge';
import { Cross2Icon } from '@radix-ui/react-icons';
import { Tooltip, TooltipTrigger, TooltipProvider } from '@frontend/shadcn/components/ui/tooltip';
import {
  TooltipHeader,
  TooltipTitle,
  TooltipBody,
  TooltipItem,
  TooltipItemKey,
  TooltipItemValue,
  TooltipContent,
} from '../../../components/tooltip';
import { DismissableBadge } from '@/applications/Social/components/filters/badges/dismissable-badge';
import { formatNumberDefault } from '@/applications/Social/utils/formatNumber';

interface MaxFollowersData {
  id: string; // Identifier for the filter, e.g., "maxFollowers"
  names: string[]; // Expects a single string element representing the number, e.g., ["500000"]
}

interface CreatorDemographicsMaxFollowersBadgeProps {
  data: MaxFollowersData;
  onDismiss: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

type CombinedProps = BadgeProps & CreatorDemographicsMaxFollowersBadgeProps;

const CreatorDemographicsMaxFollowersBadge = React.forwardRef<HTMLDivElement, CombinedProps>(
  ({ data, onDismiss, ...props }, ref) => {
    if (!data || !data.id || !data.names || data.names.length === 0 || !data.names[0]) {
      return null;
    }

    const followerValueStr = data.names[0];
    const parsedFollowers = parseInt(followerValueStr, 10);

    if (isNaN(parsedFollowers)) {
      // console.warn(`Invalid follower count string provided: ${followerValueStr}`);
      return null;
    }

    const displayValue = formatNumberDefault(parsedFollowers);

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <DismissableBadge
                ref={ref}
                key={data.id}
                id={data.id}
                variant="secondary"
                className="mr-2 mb-2"
                aria-label="Maximum followers filter"
                role="button"
                data-dd-action-name="open-max-followers-filters"
                {...props}
              >
                Max Followers: <span className="ml-1">{displayValue}</span>
                <button
                  type="button"
                  onClick={onDismiss}
                  aria-label="Remove maximum followers filter"
                  className="flex ml-1 p-1 focus:outline-none focus:ring"
                  data-dd-action-name="remove-max-followers-filters"
                >
                  <Cross2Icon className="h-3 w-3" />
                </button>
                <span className="sr-only">
                  Remove maximum followers filter for {displayValue}
                </span>
              </DismissableBadge>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <TooltipHeader>
              <TooltipTitle>Maximum Followers Filter</TooltipTitle>
            </TooltipHeader>
            <TooltipBody>
              <TooltipItem>
                <TooltipItemKey>Maximum Followers</TooltipItemKey>
                <TooltipItemValue>
                  {displayValue}
                </TooltipItemValue>
              </TooltipItem>
            </TooltipBody>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
);

CreatorDemographicsMaxFollowersBadge.displayName = 'CreatorDemographicsMaxFollowersBadge';

export default CreatorDemographicsMaxFollowersBadge;
